import { Check, Pencil, Plus, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import {
	INSPECTION_COLORS,
	InspectionPhotoField,
} from '@/components/inspections'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Field, FieldContent, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { Separator } from '@/components/ui/separator'
import { QUIZ_KINDS, type QuizKind } from '@/types/quiz'
import { convertHeicIfNeeded } from '@/utils/image-compression'
import { Spinner } from '../ui/spinner'
import { BranchSelector } from './branch-selector'

export interface Branch {
	id: string
	name: string
}

export interface Questionnaire {
	id: string
	number: string
	title: string
	kind: QuizKind
	score: number | null
	questions: QuestionInput[]
	branches: string[]
}

export interface QuestionInput {
	id?: string
	question: string
	image?: File
	imagePath?: string
	removeImage?: boolean
}

export interface QuestionnaireFormProps {
	mode: 'create' | 'edit'
	initial?: Questionnaire
	branches: Branch[]
	onSave: (data: {
		title: string
		kind: QuizKind
		score?: number
		questions: QuestionInput[]
		branches: string[]
	}) => void | Promise<void>
	onClose: () => void
}

export function QuestionnaireFormContent({
	initial,
	branches,
	onSave,
	onClose,
}: QuestionnaireFormProps) {
	const { t } = useTranslation()
	const [disableSaving, setDisableSaving] = useState(false)
	const questionInputRef = useRef<HTMLInputElement>(null)

	const isEditing = Boolean(initial)
	const [title, setTitle] = useState(initial?.title ?? '')
	const [kind, setKind] = useState<QuizKind>(initial?.kind ?? 'CHECKLIST')
	const isFreeText = kind === 'FREE_TEXT'
	const [score, setScore] = useState(initial?.score?.toString() ?? '')
	const [questions, setQuestions] = useState<QuestionInput[]>(
		initial?.questions ?? [],
	)
	const [editingQuestionIndex, setEditingQuestionIndex] = useState<
		number | null
	>(null)
	const [editingQuestionText, setEditingQuestionText] = useState('')
	const [editingQuestionImage, setEditingQuestionImage] = useState<
		File | undefined
	>(undefined)
	const [editingQuestionImagePreview, setEditingQuestionImagePreview] =
		useState<string | null>(null)
	const [editingQuestionImageRemoved, setEditingQuestionImageRemoved] =
		useState(false)
	const [
		editingQuestionImagePreviewIsObjectUrl,
		setEditingQuestionImagePreviewIsObjectUrl,
	] = useState(false)
	const [newQuestion, setNewQuestion] = useState('')
	const [newQuestionImage, setNewQuestionImage] = useState<File | undefined>(
		undefined,
	)
	const [newQuestionImagePreview, setNewQuestionImagePreview] = useState<
		string | null
	>(null)
	const [deletingQuestionIndex, setDeletingQuestionIndex] = useState<
		number | null
	>(null)
	const [selectedBranches, setSelectedBranches] = useState<string[]>(
		initial?.branches ?? branches.map((b) => b.id),
	)
	const colors = INSPECTION_COLORS.mediumGrey

	useEffect(() => {
		return () => {
			if (newQuestionImagePreview) {
				URL.revokeObjectURL(newQuestionImagePreview)
			}
			if (
				editingQuestionImagePreviewIsObjectUrl &&
				editingQuestionImagePreview
			) {
				URL.revokeObjectURL(editingQuestionImagePreview)
			}
		}
	}, [
		newQuestionImagePreview,
		editingQuestionImagePreview,
		editingQuestionImagePreviewIsObjectUrl,
	])

	const clearEditingImageState = () => {
		if (editingQuestionImagePreviewIsObjectUrl && editingQuestionImagePreview) {
			URL.revokeObjectURL(editingQuestionImagePreview)
		}
		setEditingQuestionImage(undefined)
		setEditingQuestionImagePreview(null)
		setEditingQuestionImageRemoved(false)
		setEditingQuestionImagePreviewIsObjectUrl(false)
	}

	const handleQuestionPhotoChange = async (
		e: React.ChangeEvent<HTMLInputElement>,
	) => {
		const picked = e.target.files?.[0]
		if (!picked) return
		const file = await convertHeicIfNeeded(picked)
		if (newQuestionImagePreview) {
			URL.revokeObjectURL(newQuestionImagePreview)
		}
		setNewQuestionImage(file)
		setNewQuestionImagePreview(URL.createObjectURL(file))
	}

	const handleRemoveQuestionPhoto = () => {
		setNewQuestionImage(undefined)
		if (newQuestionImagePreview) {
			URL.revokeObjectURL(newQuestionImagePreview)
		}
		setNewQuestionImagePreview(null)
	}

	const handleAddQuestion = () => {
		const trimmed = newQuestion.trim()
		if (!trimmed) {
			toast.error(t('administration.questionnairesForm.emptyQuestionError'))
			return
		}
		setQuestions((prev) => [
			...prev,
			{ question: trimmed, image: newQuestionImage },
		])
		setNewQuestion('')
		handleRemoveQuestionPhoto()
		// keep focus in the input for rapid entry
		requestAnimationFrame(() => questionInputRef.current?.focus())
	}

	const handleRemoveQuestion = (index: number) => {
		if (editingQuestionIndex === index) {
			setEditingQuestionIndex(null)
			setEditingQuestionText('')
			clearEditingImageState()
		}
		setQuestions((prev) => prev.filter((_, i) => i !== index))
	}

	const handleConfirmRemoveQuestion = () => {
		if (deletingQuestionIndex === null) {
			return
		}

		handleRemoveQuestion(deletingQuestionIndex)
		setDeletingQuestionIndex(null)
	}

	const handleStartEditingQuestion = (index: number) => {
		clearEditingImageState()
		const question = questions[index]
		setEditingQuestionIndex(index)
		setEditingQuestionText(question?.question ?? '')

		if (question?.image instanceof File) {
			setEditingQuestionImage(question.image)
			const preview = URL.createObjectURL(question.image)
			setEditingQuestionImagePreview(preview)
			setEditingQuestionImagePreviewIsObjectUrl(true)
			return
		}

		if (question?.imagePath) {
			setEditingQuestionImagePreview(question.imagePath)
			setEditingQuestionImagePreviewIsObjectUrl(false)
		}
	}

	const handleCancelEditingQuestion = () => {
		setEditingQuestionIndex(null)
		setEditingQuestionText('')
		clearEditingImageState()
	}

	const handleEditingQuestionPhotoChange = async (
		e: React.ChangeEvent<HTMLInputElement>,
	) => {
		const picked = e.target.files?.[0]
		if (!picked) {
			return
		}

		const file = await convertHeicIfNeeded(picked)
		if (editingQuestionImagePreviewIsObjectUrl && editingQuestionImagePreview) {
			URL.revokeObjectURL(editingQuestionImagePreview)
		}

		setEditingQuestionImage(file)
		setEditingQuestionImagePreview(URL.createObjectURL(file))
		setEditingQuestionImagePreviewIsObjectUrl(true)
		setEditingQuestionImageRemoved(false)
	}

	const handleRemoveEditingQuestionPhoto = () => {
		if (editingQuestionImagePreviewIsObjectUrl && editingQuestionImagePreview) {
			URL.revokeObjectURL(editingQuestionImagePreview)
		}
		setEditingQuestionImage(undefined)
		setEditingQuestionImagePreview(null)
		setEditingQuestionImagePreviewIsObjectUrl(false)
		setEditingQuestionImageRemoved(true)
	}

	const handleSaveEditingQuestion = () => {
		if (editingQuestionIndex === null) {
			return
		}

		const trimmed = editingQuestionText.trim()
		if (!trimmed) {
			toast.error(t('administration.questionnairesForm.emptyQuestionError'))
			return
		}

		setQuestions((prev) =>
			prev.map((q, i) =>
				i === editingQuestionIndex
					? (() => {
							const { imagePath: _imagePath, ...questionWithoutImagePath } = q

							if (editingQuestionImageRemoved || editingQuestionImage) {
								return {
									...questionWithoutImagePath,
									question: trimmed,
									image: editingQuestionImage,
									removeImage: editingQuestionImageRemoved || undefined,
								}
							}

							return {
								...questionWithoutImagePath,
								question: trimmed,
								image: undefined,
								removeImage: undefined,
								...(q.imagePath ? { imagePath: q.imagePath } : {}),
							}
						})()
					: q,
			),
		)
		handleCancelEditingQuestion()
	}

	const handleSave = async () => {
		if (disableSaving) {
			return
		}

		let finalQuestions = [...questions]

		if (editingQuestionIndex !== null) {
			const trimmedEditingQuestion = editingQuestionText.trim()
			if (!trimmedEditingQuestion) {
				toast.error(t('administration.questionnairesForm.emptyQuestionError'))
				return
			}

			finalQuestions = finalQuestions.map((q, i) =>
				i === editingQuestionIndex
					? (() => {
							const { imagePath: _imagePath, ...questionWithoutImagePath } = q

							if (editingQuestionImageRemoved || editingQuestionImage) {
								return {
									...questionWithoutImagePath,
									question: trimmedEditingQuestion,
									image: editingQuestionImage,
									removeImage: editingQuestionImageRemoved || undefined,
								}
							}

							return {
								...questionWithoutImagePath,
								question: trimmedEditingQuestion,
								image: undefined,
								removeImage: undefined,
								...(q.imagePath ? { imagePath: q.imagePath } : {}),
							}
						})()
					: q,
			)
		}

		const trimmedNewQuestion = newQuestion.trim()

		if (trimmedNewQuestion) {
			finalQuestions = [
				...finalQuestions,
				{ question: trimmedNewQuestion, image: newQuestionImage },
			]
		}

		if (newQuestionImage && !trimmedNewQuestion) {
			if (finalQuestions.length === 1 && !finalQuestions[0].image) {
				finalQuestions = [{ ...finalQuestions[0], image: newQuestionImage }]
			} else {
				toast.error(t('administration.questionnairesForm.pendingImageError'))
				return
			}
		}

		if (!title.trim()) {
			toast.error(t('administration.questionnairesForm.emptyNameError'))
			return
		}
		if (finalQuestions.length === 0) {
			toast.error(t('administration.questionnairesForm.noQuestionsError'))
			return
		}
		if (selectedBranches.length === 0) {
			toast.error(t('administration.questionnairesForm.noBranchesError'))
			return
		}
		// vazio = mantém a nota vigente (ou segue sem nota)
		// resposta livre não tem conforme/não conforme, então não tem nota
		const parsedScore =
			isFreeText || score.trim() === '' ? undefined : Number(score)
		if (
			parsedScore !== undefined &&
			(!Number.isInteger(parsedScore) || parsedScore < 1 || parsedScore > 100)
		) {
			toast.error(t('administration.questionnairesForm.invalidScoreError'))
			return
		}

		setDisableSaving(true)
		try {
			const questionsToSave: QuestionInput[] = finalQuestions.map((q) => {
				const baseQuestion: QuestionInput = {
					id: q.id,
					question: q.question,
					image: q.image,
					removeImage: q.removeImage,
				}

				if (!q.image && q.imagePath) {
					baseQuestion.imagePath = q.imagePath
				}

				return baseQuestion
			})

			await Promise.resolve(
				onSave({
					title: title.trim(),
					kind,
					score: parsedScore,
					questions: questionsToSave,
					branches: selectedBranches,
				}),
			)
		} catch (error) {
			toast.error(`Erro ao salvar ${String(error)}`)
		}
	}

	return (
		<>
			<div className="flex flex-col gap-5 px-1">
				{/* Questionnaire name */}
				<Field>
					<FieldLabel htmlFor="questionnaire-title">
						{t('administration.questionnairesForm.questionnaireName')}
					</FieldLabel>
					<FieldContent>
						<Input
							id="questionnaire-title"
							value={title}
							onChange={(e) => setTitle(e.target.value)}
							placeholder={t(
								'administration.questionnairesForm.questionnaireNamePlaceholder',
							)}
							autoFocus={false}
						/>
					</FieldContent>
				</Field>

				{/* Kind: definido na criação e travado depois, as respostas gravadas dependem dele */}
				<Field>
					<FieldLabel htmlFor="questionnaire-kind">
						{t('administration.questionnairesForm.kindLabel')}
					</FieldLabel>
					<FieldContent>
						<NativeSelect
							id="questionnaire-kind"
							value={kind}
							disabled={isEditing}
							onChange={(e) => setKind(e.target.value as QuizKind)}
						>
							{QUIZ_KINDS.map((option) => (
								<NativeSelectOption key={option} value={option}>
									{t(`administration.questionnairesForm.kind_${option}`)}
								</NativeSelectOption>
							))}
						</NativeSelect>
						<p className="text-xs text-muted-foreground">
							{t(
								isEditing
									? 'administration.questionnairesForm.kindLockedHint'
									: `administration.questionnairesForm.kindHint_${kind}`,
							)}
						</p>
					</FieldContent>
				</Field>

				{/* Score */}
				{!isFreeText && (
					<Field>
						<FieldLabel htmlFor="questionnaire-score">
							{t('administration.questionnairesForm.scoreLabel')}
						</FieldLabel>
						<FieldContent>
							<Input
								id="questionnaire-score"
								type="number"
								min={1}
								max={100}
								step={1}
								value={score}
								onChange={(e) => setScore(e.target.value)}
								placeholder={t(
									'administration.questionnairesForm.scorePlaceholder',
								)}
								className="w-32"
							/>
							<p className="text-xs text-muted-foreground">
								{t('administration.questionnairesForm.scoreHint')}
							</p>
						</FieldContent>
					</Field>
				)}

				<Separator className="bg-border/60" />

				{/* Questions section */}
				<div className="space-y-3">
					<div className="flex items-center justify-between">
						<span className="text-sm font-semibold text-kaefer-dark-grey">
							{t('administration.questionnairesForm.questionsList')}
						</span>
						<span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-kaefer-medium-grey/15 px-1.5 text-[11px] font-semibold tabular-nums text-kaefer-medium-grey">
							{questions.length}
						</span>
					</div>

					{/* Existing questions */}
					{questions.length > 0 && (
						<div className="max-h-40 space-y-1.5 overflow-y-auto">
							{questions.map((q, i) => (
								<div
									key={i}
									className="flex min-w-0 items-start gap-2 rounded-md border border-kaefer-light-silver/60 bg-muted/30 px-3 py-2 text-sm"
								>
									<span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-kaefer-medium-grey/10 text-[10px] font-bold text-kaefer-medium-grey">
										{i + 1}
									</span>
									{editingQuestionIndex === i ? (
										<div className="min-w-0 flex-1 space-y-2">
											<Input
												value={editingQuestionText}
												onChange={(e) => setEditingQuestionText(e.target.value)}
												className="h-8 w-full"
												onKeyDown={(e) => {
													if (e.key === 'Enter') {
														e.preventDefault()
														handleSaveEditingQuestion()
													}
													if (e.key === 'Escape') {
														e.preventDefault()
														handleCancelEditingQuestion()
													}
												}}
											/>
											<InspectionPhotoField
												attachLabel={t(
													'administration.questionnairesForm.attachQuestionImage',
												)}
												altText={t(
													'administration.questionnairesForm.questionImageSelected',
												)}
												preview={editingQuestionImagePreview}
												onFileChange={handleEditingQuestionPhotoChange}
												onRemove={handleRemoveEditingQuestionPhoto}
												colors={colors}
												compact
											/>
										</div>
									) : (
										<span className="min-w-0 flex-1 wrap-break-word text-kaefer-dark-grey">
											{q.question}
										</span>
									)}
									{(editingQuestionIndex === i
										? Boolean(editingQuestionImagePreview)
										: Boolean(q.image || q.imagePath)) && (
										<span className="shrink-0 rounded-full bg-kaefer-medium-grey/10 px-2 py-0.5 text-[10px] font-semibold text-kaefer-medium-grey">
											{t('administration.questionnairesForm.questionHasImage')}
										</span>
									)}
									{editingQuestionIndex === i ? (
										<>
											<Button
												type="button"
												variant="ghost"
												size="icon-circle-sm"
												onClick={handleSaveEditingQuestion}
												className="text-kaefer-medium-grey hover:bg-transparent hover:text-kaefer-medium-grey/70 rounded-sm"
											>
												<Check />
											</Button>
											<Button
												type="button"
												variant="ghost"
												size="icon-circle-sm"
												onClick={handleCancelEditingQuestion}
												className="text-kaefer-medium-grey hover:bg-transparent hover:text-kaefer-medium-grey/70 rounded-sm"
											>
												<X />
											</Button>
										</>
									) : (
										<Button
											type="button"
											variant="ghost"
											size="icon-circle-sm"
											onClick={() => handleStartEditingQuestion(i)}
											className="text-kaefer-medium-grey hover:bg-transparent hover:text-kaefer-medium-grey/70 rounded-sm"
										>
											<Pencil />
										</Button>
									)}
									{editingQuestionIndex !== i && (
										<Button
											type="button"
											variant="ghost"
											size="icon-circle-sm"
											onClick={() => setDeletingQuestionIndex(i)}
											className="text-kaefer-red hover:bg-transparent hover:text-kaefer-red/70 hover:border hover:border-kaefer-red/70 rounded-sm"
										>
											<X />
										</Button>
									)}
								</div>
							))}
						</div>
					)}

					{/* Add new question */}
					<div className="flex gap-2">
						<Input
							ref={questionInputRef}
							value={newQuestion}
							onChange={(e) => setNewQuestion(e.target.value)}
							placeholder={t(
								'administration.questionnairesForm.addQuestionPlaceholder',
							)}
							className="h-9 flex-1"
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									e.preventDefault()
									handleAddQuestion()
								}
							}}
						/>
						<Button
							type="button"
							variant="ghost"
							size="xs"
							onClick={handleAddQuestion}
							className="gap-1.5 text-kaefer-medium-grey hover:text-kaefer-medium-grey/80"
						>
							<Plus className="size-3.5" />
							{t('administration.questionnairesForm.includeButton')}
						</Button>
					</div>

					<InspectionPhotoField
						attachLabel={t(
							'administration.questionnairesForm.attachQuestionImage',
						)}
						altText={t(
							'administration.questionnairesForm.questionImageSelected',
						)}
						preview={newQuestionImagePreview}
						onFileChange={handleQuestionPhotoChange}
						onRemove={handleRemoveQuestionPhoto}
						colors={colors}
					/>
				</div>

				<Separator className="bg-border/60" />

				{/* Branches section */}
				<BranchSelector
					branches={branches}
					selected={selectedBranches}
					onChange={setSelectedBranches}
				/>

				{/* Footer buttons */}
				<div className="mt-auto flex gap-3 pt-1 sticky bottom-0 bg-background py-2">
					<Button
						type="button"
						variant="cancel"
						onClick={onClose}
						className="flex-1"
					>
						{t('administration.questionnairesForm.cancelButton')}
					</Button>
					<Button
						type="button"
						onClick={handleSave}
						disabled={disableSaving}
						className="flex-1 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90"
					>
						{disableSaving ? (
							<>
								<Spinner /> {t('administration.questionnairesForm.saving')}
							</>
						) : (
							t('administration.questionnairesForm.saveButton')
						)}
					</Button>
				</div>
			</div>

			<AlertDialog
				open={deletingQuestionIndex !== null}
				onOpenChange={(open) => {
					if (!open) {
						setDeletingQuestionIndex(null)
					}
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Confirmar exclusao</AlertDialogTitle>
						<AlertDialogDescription>
							Deseja realmente excluir a pergunta
							{deletingQuestionIndex !== null
								? ` "${questions[deletingQuestionIndex]?.question ?? ''}"`
								: ''}
							?
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
						<AlertDialogAction onClick={handleConfirmRemoveQuestion}>
							{t('common.delete')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
